/* eslint-disable react/no-array-index-key */
import React, {FunctionComponent, ReactElement, useContext} from 'react';
import {Property as CSSProperty} from 'csstype';

import {ReactComponent as BackgroundCircleDoughnut} from '../../../../images/backgroundCircle.svg';
import {ReactComponent as BackgroundCircleSolid} from '../../../../images/backgroundCircleSolid.svg';
import DeviceContext, {Device} from '../../../../RootProvider/DeviceContext';

export enum LayoutCircleType {
  BLUE = 'blue',
  BLUE_BRIGHT = 'blueBright',
  DARK = 'dark',
  MEDIUM = 'medium',
  LIGHT = 'light',
  YELLOW = 'yellow',
  YELLOW_BRIGHT = 'yellowBright',
}

// allowed js styles
export interface LayoutCircleStyles {
  top?: CSSProperty.Top;
  right?: CSSProperty.Right;
  bottom?: CSSProperty.Bottom;
  left?: CSSProperty.Left;
  width?: CSSProperty.Width;
  height?: CSSProperty.Height;
  marginBottom?: CSSProperty.MarginBottom;
  marginLeft?: CSSProperty.MarginLeft;
  marginRight?: CSSProperty.MarginRight;
  marginTop?: CSSProperty.MarginTop;
  transform?: CSSProperty.Transform;
  transition?: CSSProperty.Transition;
  zIndex?: CSSProperty.ZIndex;
  opacity?: CSSProperty.Opacity;
}

export interface LayoutCircleProps {
  style: LayoutCircleStyles;
  img?: FunctionComponent;
  type: LayoutCircleType;
  isSolid?: boolean;
  multiple?: {
    rows: number;
    columns: number;
  };
}

export interface LayoutCircleConfig {
  desktop?: LayoutCircleProps[];
  tablet?: LayoutCircleProps[];
  mobile?: LayoutCircleProps[];
  allDevices?: LayoutCircleProps[];
}

interface Props {
  config: LayoutCircleConfig;
}

function LayoutSectionCircles({config}: Props): ReactElement | null {
  const device = useContext(DeviceContext);
  let circles: LayoutCircleProps[] = config.allDevices || [];
  // use device config
  switch (device) {
    case Device.MOBILE:
      circles = [...circles, ...(config.mobile || [])];
      break;

    case Device.TABLET:
      circles = [...circles, ...(config.tablet || [])];
      break;

    case Device.DESKTOP:
      circles = [...circles, ...(config.desktop || [])];
      break;

    default:
      return null;
  }

  // skip blank configs
  if (!circles || !circles.length) {
    return null;
  }

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {circles.map(({style, type, multiple, isSolid, img: Img}, index) => {
        const className = `layoutSection__circle layoutSection__circle--${type} ${
          Img ? 'layoutSection__circle--img' : ''
        }`.trim();

        let Circle = isSolid ? BackgroundCircleSolid : BackgroundCircleDoughnut;

        if (Img) {
          Circle = Img;
        }

        if (multiple) {
          return (
            <div className="layoutCircleMulti" style={style} key={`layoutCircle-multi-${index}`}>
              {[...Array(multiple.rows)].map((_, row) => (
                <div key={`layoutCircle-multi-row-${row}`} className="layoutCircleMulti__row">
                  {[...Array(multiple.columns)].map((__, col) => {
                    return <Circle className={className} key={`layoutCircle-multi-col-${row}-${col}`} style={style} />;
                  })}
                </div>
              ))}
            </div>
          );
        }

        return <Circle key={`layoutCircle-${index}`} className={className} style={style} />;
      })}
    </>
  );
}

export default LayoutSectionCircles;
